import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, 
  Minus, Globe, Users, Building2, MapPin, FileText, Sparkles,
  ChevronDown, ChevronRight, AlertCircle, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsAnalysisPanel = ({ token }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [intelligenceBrief, setIntelligenceBrief] = useState(null);
  const [expandedArticle, setExpandedArticle] = useState(null);
  const [maxArticles, setMaxArticles] = useState(50);

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'negative': return <TrendingDown className="w-4 h-4 text-red-400" />;
      case 'alarming': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-400 bg-green-500/20 border-green-500/50';
      case 'negative': return 'text-red-400 bg-red-500/20 border-red-500/50';
      case 'alarming': return 'text-red-500 bg-red-500/30 border-red-500';
      default: return 'text-slate-400 bg-slate-500/20 border-slate-500/50';
    }
  };

  const getAlertColor = (level) => {
    switch (level) {
      case 'critical': return 'text-red-500 bg-red-500/30 border-red-500';
      case 'high': return 'text-orange-400 bg-orange-500/20 border-orange-500/50';
      case 'medium': return 'text-amber-400 bg-amber-500/20 border-amber-500/50';
      case 'low': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
      default: return 'text-green-400 bg-green-500/20 border-green-500/50';
    }
  };

  const analyzeNews = async () => {
    setIsAnalyzing(true);
    setAnalysisResult(null);
    setIntelligenceBrief(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/news/analyze`,
        { max_articles: maxArticles },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minutes
        }
      );
      
      setAnalysisResult(response.data);
      toast.success(`${response.data.analyzed_count} articles analysés`);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const generateBrief = async () => {
    if (!analysisResult?.analyses) return;
    
    setIsGeneratingBrief(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/news/brief`,
        { analyses: analysisResult.analyses },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      );
      
      setIntelligenceBrief(response.data.brief);
      toast.success('Brief de renseignement généré');
      
    } catch (err) {
      toast.error('Erreur lors de la génération du brief');
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-purple-500/30 rounded-lg p-4">
      {/* Explication du fonctionnement */}
      <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded">
        <p className="font-mono text-purple-300 text-xs">
          <strong>SCAN OSINT → BRIEF :</strong> Cette analyse scrute les sources configurées et génère un BRIEF de renseignement.
          Sélectionnez le nombre d'articles à analyser, puis cliquez sur ANALYSER. Une fois l'analyse terminée, 
          générez le BRIEF pour obtenir une synthèse stratégique exploitable.
        </p>
      </div>
      
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-purple-400" />
          <span className="font-mono font-bold text-purple-400">ANALYSE IA DES ACTUALITÉS</span>
          <span className="text-xs text-slate-500 font-mono">(Claude Sonnet)</span>
        </div>
        
        <div className="flex items-center gap-2">
          <select
            value={maxArticles}
            onChange={(e) => setMaxArticles(parseInt(e.target.value))}
            className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm text-white font-mono"
            data-testid="max-articles-select"
          >
            <option value={25}>25 articles</option>
            <option value={50}>50 articles</option>
            <option value={100}>100 articles</option>
            <option value={200}>200 articles (complet)</option>
          </select>
          
          <button
            onClick={analyzeNews}
            disabled={isAnalyzing}
            className="px-4 py-2 bg-purple-500/20 border border-purple-500 text-purple-400 rounded font-mono text-sm hover:bg-purple-500/30 disabled:opacity-50 flex items-center gap-2"
            data-testid="analyze-news-btn"
          >
            {isAnalyzing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                ANALYSE...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                ANALYSER
              </>
            )}
          </button>
        </div>
      </div>

      {/* Loading state with console */}
      {isAnalyzing && (
        <div className="bg-black/50 rounded p-3 font-mono text-xs space-y-1 mb-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-green-400">
            [RSS] Récupération des articles en cours...
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="text-cyan-400">
            [LLM] Connexion à Claude Sonnet...
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="text-purple-400">
            [AI] Analyse des sentiments et extraction d'entités...
          </motion.div>
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: [0, 1, 0] }}
            transition={{ delay: 3, duration: 1, repeat: Infinity }}
            className="text-white"
          >
            █ Traitement en cours (peut prendre jusqu'à 2 min)...
          </motion.div>
        </div>
      )}

      {/* Results */}
      {analysisResult && (
        <div className="space-y-4">
          {/* Statistics */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-slate-700/30 rounded p-3 text-center">
              <div className="text-2xl font-bold text-cyan-400">{analysisResult.analyzed_count}</div>
              <div className="text-xs text-slate-400 font-mono">ARTICLES</div>
            </div>
            <div className={`rounded p-3 text-center border ${getSentimentColor(analysisResult.statistics?.dominant_sentiment)}`}>
              <div className="text-lg font-bold uppercase">{analysisResult.statistics?.dominant_sentiment}</div>
              <div className="text-xs opacity-80 font-mono">SENTIMENT</div>
            </div>
            <div className={`rounded p-3 text-center border ${analysisResult.statistics?.alert_count > 0 ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'bg-green-500/20 border-green-500/50 text-green-400'}`}>
              <div className="text-2xl font-bold">{analysisResult.statistics?.alert_count || 0}</div>
              <div className="text-xs opacity-80 font-mono">ALERTES</div>
            </div>
            <div className="bg-slate-700/30 rounded p-3 text-center">
              <div className="text-2xl font-bold text-purple-400">{Object.keys(analysisResult.statistics?.zones_mentioned || {}).length}</div>
              <div className="text-xs text-slate-400 font-mono">ZONES</div>
            </div>
          </div>

          {/* Alerts summary */}
          {analysisResult.alerts?.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="font-mono text-red-400 text-sm font-bold">ALERTES DÉTECTÉES</span>
              </div>
              <div className="space-y-2">
                {analysisResult.alerts.map((alert, idx) => (
                  <div key={idx} className={`p-2 rounded border ${getAlertColor(alert.alert_level)}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold uppercase">{alert.alert_level}</span>
                      <span className="text-xs opacity-70">{alert.source}</span>
                    </div>
                    <div className="text-xs mt-1">{alert.alert_reason}</div>
                    <div className="flex gap-1 mt-1">
                      {alert.zones?.map((zone, zidx) => (
                        <span key={zidx} className="px-1 py-0.5 bg-black/30 rounded text-xs">{zone}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top entities */}
          {analysisResult.statistics?.top_entities?.length > 0 && (
            <div className="bg-slate-700/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-cyan-400" />
                <span className="font-mono text-cyan-400 text-sm font-bold">ENTITÉS CLÉS</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {analysisResult.statistics.top_entities.slice(0, 10).map((entity, idx) => (
                  <span 
                    key={idx} 
                    className={`px-2 py-1 rounded text-xs font-mono ${
                      entity.type === 'country' ? 'bg-blue-500/20 text-blue-400' :
                      entity.type === 'org' ? 'bg-purple-500/20 text-purple-400' :
                      entity.type === 'person' ? 'bg-green-500/20 text-green-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}
                  >
                    {entity.name} ({entity.mentions})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Article analyses */}
          <div className="space-y-2">
            <div className="font-mono text-slate-400 text-sm">ANALYSES DÉTAILLÉES</div>
            {analysisResult.analyses?.map((analysis, idx) => (
              <div 
                key={idx}
                className="bg-slate-700/30 rounded-lg border border-slate-600/50 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedArticle(expandedArticle === idx ? null : idx)}
                  className="w-full p-3 flex items-center justify-between text-left hover:bg-slate-700/50"
                >
                  <div className="flex items-center gap-2">
                    {getSentimentIcon(analysis.sentiment)}
                    <span className="text-sm text-white">{analysis.title?.substring(0, 60)}...</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold border ${getAlertColor(analysis.alert_level)}`}>
                      {analysis.alert_level?.toUpperCase()}
                    </span>
                    {expandedArticle === idx ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </div>
                </button>
                
                <AnimatePresence>
                  {expandedArticle === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-600/50"
                    >
                      <div className="p-3 space-y-3">
                        <div>
                          <span className="text-xs text-slate-400 font-mono">RÉSUMÉ</span>
                          <p className="text-sm text-white mt-1">{analysis.summary}</p>
                        </div>
                        
                        {analysis.key_facts?.length > 0 && (
                          <div>
                            <span className="text-xs text-slate-400 font-mono">FAITS CLÉS</span>
                            <ul className="mt-1 space-y-1">
                              {analysis.key_facts.map((fact, fidx) => (
                                <li key={fidx} className="text-xs text-slate-300 flex items-start gap-1">
                                  <CheckCircle2 className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                                  {fact}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {analysis.geopolitical_implications?.length > 0 && (
                          <div>
                            <span className="text-xs text-slate-400 font-mono">IMPLICATIONS GÉOPOLITIQUES</span>
                            <ul className="mt-1 space-y-1">
                              {analysis.geopolitical_implications.map((impl, iidx) => (
                                <li key={iidx} className="text-xs text-amber-400">• {impl}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3 text-cyan-400" />
                          <span className="text-xs text-slate-400">Zones:</span>
                          {analysis.relevant_zones?.map((zone, zidx) => (
                            <span key={zidx} className="px-1 py-0.5 bg-cyan-500/20 text-cyan-400 rounded text-xs">
                              {zone}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Generate brief button */}
          <button
            onClick={generateBrief}
            disabled={isGeneratingBrief || !analysisResult?.analyses?.length}
            className="w-full py-3 bg-green-500/20 border border-green-500 text-green-400 rounded font-mono text-sm hover:bg-green-500/30 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="generate-brief-btn"
          >
            {isGeneratingBrief ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                GÉNÉRATION DU BRIEF...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                GÉNÉRER BRIEF DE RENSEIGNEMENT
              </>
            )}
          </button>

          {/* Intelligence brief */}
          {intelligenceBrief && (
            <div className="bg-slate-900 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-green-400" />
                <span className="font-mono text-green-400 font-bold">BRIEF DE RENSEIGNEMENT</span>
              </div>
              <div className="prose prose-invert prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-slate-300 font-mono bg-black/30 p-3 rounded">
                  {intelligenceBrief}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!isAnalyzing && !analysisResult && (
        <div className="text-center py-8 text-slate-400">
          <Brain className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="font-mono text-sm">Cliquez sur ANALYSER pour lancer l'analyse IA des actualités</p>
          <p className="text-xs mt-1 text-slate-500">Claude Sonnet analysera les sentiments, entités et alertes</p>
        </div>
      )}
    </div>
  );
};

export default NewsAnalysisPanel;
